import {
  Autocomplete,
  AutocompleteProps,
  Box,
  CircularProgress,
  FormControl,
  TextField,
} from '@mui/material';
import {
  HTMLAttributes,
  ReactElement,
  ReactNode,
  cloneElement,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { FixedSizeList, ListChildComponentProps, ListOnItemsRenderedProps } from 'react-window';
import _ from 'lodash';
import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from 'app/store';
import { DEFAULT_LANDING_PAGE, savePaidolSessionId } from 'app/store/authSlice';
import { searchPaidolsAsSupport, selectUserCompanies } from 'app/store/userCompaniesSlice';

type MappedPaidol = {
  paidolId?: string;
  name?: string;
};

const ROW_HEIGHT = 36;
const MAX_VISIBLE_ROWS = 8;
const SEARCH_DEBOUNCE_MS = 250;
// Fire load-more when the user scrolls within this many rows of the end.
// Three rows of buffer at a row height of 36px gives the request time to
// resolve before the user hits an empty bottom.
const LOAD_MORE_THRESHOLD_ROWS = 3;

type LoadMoreContextValue = {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
};
// Default no-op so non-support users (who don't need pagination) render the
// listbox normally without any provider wiring.
const LoadMoreContext = createContext<LoadMoreContextValue>({
  onLoadMore: () => undefined,
  hasMore: false,
  isLoading: false,
});

// Custom Autocomplete listbox that virtualizes its children via react-window.
// Without this, support users with thousands of paidols would render every
// matching <li> into the DOM. We render only the visible window plus a small
// overscan buffer, and fire `onLoadMore` from context when the user scrolls
// near the end so server-side pagination feels like one continuous list.
const VirtualizedListbox = forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(
  function VirtualizedListbox(props, ref) {
    const { children, ...rest } = props;
    const items = (Array.isArray(children) ? children : [children]) as ReactElement[];
    const itemCount = items.length;
    const height = Math.min(itemCount, MAX_VISIBLE_ROWS) * ROW_HEIGHT;

    const { onLoadMore, hasMore, isLoading } = useContext(LoadMoreContext);

    const renderRow = ({ index, style }: ListChildComponentProps) => {
      const child = items[index];
      // The child carries its own MUI props (key, role, onClick, etc.) — clone
      // so we can stamp on the absolute positioning the virtual list provides.
      return cloneElement(child, { style: { ...child.props.style, ...style } });
    };

    const handleItemsRendered = useCallback(
      ({ visibleStopIndex }: ListOnItemsRenderedProps) => {
        if (!hasMore || isLoading) return;
        if (visibleStopIndex >= itemCount - LOAD_MORE_THRESHOLD_ROWS) {
          onLoadMore();
        }
      },
      [hasMore, isLoading, itemCount, onLoadMore]
    );

    return (
      <div ref={ref} {...rest}>
        <FixedSizeList
          height={height || ROW_HEIGHT}
          itemCount={itemCount}
          itemSize={ROW_HEIGHT}
          width="100%"
          overscanCount={4}
          onItemsRendered={handleItemsRendered}
        >
          {renderRow}
        </FixedSizeList>
      </div>
    );
  }
);

// Bold the portion of `text` that matches `query` (case-insensitive, first
// occurrence anywhere in the string) so the dropdown highlights what the user
// searched — e.g. searching "nstruction" bolds it inside "Construction Co".
const highlightMatch = (text: string, query: string): ReactNode => {
  const trimmed = query.trim();
  if (!trimmed) return text;
  const idx = text.toLowerCase().indexOf(trimmed.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <strong>{text.slice(idx, idx + trimmed.length)}</strong>
      {text.slice(idx + trimmed.length)}
    </>
  );
};

function CompanySwitcher({ sx = {} }: { sx?: AutocompleteProps<MappedPaidol, false, false, false>['sx'] }) {
  const {
    isSwitching,
    isUserOnboarded,
    paidolUsers,
    selectedPaidol,
    supportSearchResults,
    supportSearchLoading,
    supportSearchNextToken,
    supportSearchQuery,
    user,
  } = useAppSelector(selectUserCompanies);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const isSupport = Boolean(user?.is_support);

  const handleChange = async (newValue: MappedPaidol) => {
    if (!newValue.paidolId) return;

    try {
      await dispatch(savePaidolSessionId(newValue.paidolId)).unwrap();
      // Navigate to the landing page rather than reloading the current URL —
      // entity routes (e.g. /transactions/abc-123) often don't exist in the
      // newly selected paidol and would render a blank state.
      window.location.href = DEFAULT_LANDING_PAGE;
    } catch {
      // Surface the failure so the user knows the click did something. The
      // SEE-861 backend check rejects with 403 for non-members — the most
      // common reason a switch is denied — but any thunk rejection lands here.
      enqueueSnackbar('Could not switch companies. Please try again.', {
        variant: 'error',
      });
    }
  };

  const debouncedSearch = useRef(
    debounce((query: string) => {
      dispatch(searchPaidolsAsSupport({ query }));
    }, SEARCH_DEBOUNCE_MS)
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleInputChange = useCallback(
    (_event: unknown, value: string, reason: string) => {
      // Don't refetch when the input changes because the user just picked an
      // option (`reason === 'reset'`) — that would clobber the loading state
      // mid-switch.
      if (reason === 'reset') return;
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  // Pre-load the first page of results when the dropdown opens. Also restores
  // the default alphabetical list if the state still holds a previous search
  // (e.g. the close-time reset was skipped because the close came from picking
  // an option). Cheap (limit=25); the server scopes results to the caller —
  // support users see all companies, regular users only their own.
  const handleOpen = useCallback(() => {
    if (supportSearchLoading) return;
    if (supportSearchResults.length === 0 || supportSearchQuery) {
      dispatch(searchPaidolsAsSupport({ query: '' }));
    }
  }, [dispatch, supportSearchResults.length, supportSearchLoading, supportSearchQuery]);

  // Closing the dropdown ends the search session: drop any pending debounced
  // fetch and put the default alphabetical first page back so the next open
  // doesn't show the previous search's results. Skipped when the close comes
  // from selecting an option — the switch navigates away, and if it fails,
  // handleOpen's stale-query check restores the list on the next open.
  const handleClose = useCallback(
    (_event: unknown, reason: string) => {
      debouncedSearch.cancel();
      if (reason === 'selectOption') return;
      if (supportSearchQuery) {
        dispatch(searchPaidolsAsSupport({ query: '' }));
      }
    },
    [debouncedSearch, supportSearchQuery, dispatch]
  );

  // Single-company regular users never see the picker (the render below bails
  // out), so don't burn a search request on their behalf. Support users always
  // get it; regular users only once their membership list shows >1 company.
  const showSwitcher = isSupport || paidolUsers.length > 1;

  // Pre-warm the first page at mount rather than at first open. Removes the
  // visible "list pops in" delay when the user clicks the picker. Idempotent —
  // handleOpen still re-fires if empty.
  const hasPreloadedRef = useRef(false);
  useEffect(() => {
    if (!showSwitcher) return;
    if (!hasPreloadedRef.current && supportSearchResults.length === 0 && !supportSearchLoading) {
      hasPreloadedRef.current = true;
      dispatch(searchPaidolsAsSupport({ query: '' }));
    }
  }, [showSwitcher, supportSearchResults.length, supportSearchLoading, dispatch]);

  // Scroll-to-bottom pagination. The slice's append-vs-replace logic keys off
  // whether `nextToken` is present in the arg, so passing the current token
  // here continues the same query rather than resetting to page 1. The
  // `supportSearchQuery == null` short-circuit guards against firing into a
  // freshly reset session (nextToken should also be null after reset, but
  // belt-and-braces).
  const handleLoadMore = useCallback(() => {
    if (supportSearchQuery == null) return;
    if (!supportSearchNextToken || supportSearchLoading) return;
    dispatch(
      searchPaidolsAsSupport({
        query: supportSearchQuery,
        nextToken: supportSearchNextToken,
      })
    );
  }, [dispatch, supportSearchLoading, supportSearchNextToken, supportSearchQuery]);

  const loadMoreContextValue = useMemo<LoadMoreContextValue>(
    () => ({
      onLoadMore: handleLoadMore,
      hasMore: Boolean(supportSearchNextToken),
      isLoading: supportSearchLoading,
    }),
    [handleLoadMore, supportSearchLoading, supportSearchNextToken]
  );

  // One options source for everyone: the server-side switcher search, which the
  // backend scopes to the caller (support -> all companies, regular users ->
  // their own memberships) and returns sorted alphabetically across the full
  // scoped set. Client-side merging of the membership list here would inject
  // items from beyond the loaded page and break that global ordering.
  const userCompanies = useMemo(() => {
    const mappedSearch: MappedPaidol[] = supportSearchResults.map(({ id: paidolId, name }) => ({
      paidolId,
      name,
    }));

    // Always include the current selection so MUI Autocomplete's
    // isOptionEqualToValue check finds a match — otherwise the picker shows
    // an empty input on first paint when search results haven't loaded yet.
    // (filterOptions hides it whenever it doesn't match an active search.)
    const mappedSelected: MappedPaidol[] = selectedPaidol?.id
      ? [{ paidolId: selectedPaidol.id, name: selectedPaidol.name ?? '' }]
      : [];

    // Case-insensitive sort to match the server's ordering, so the injected
    // selection lands in the right spot instead of after all uppercase names.
    return _.orderBy(
      _.uniqWith([...mappedSelected, ...mappedSearch], (a, b) => a.paidolId === b.paidolId),
      [(option) => option.name?.toLowerCase()],
      ['asc']
    );
  }, [supportSearchResults, selectedPaidol]);

  const selectedPaidolValue: MappedPaidol = useMemo(
    () => ({ paidolId: selectedPaidol?.id, name: selectedPaidol?.name }),
    [selectedPaidol]
  );

  // Regular users with at most one company have nothing to switch to — keep the
  // picker hidden for them, judged from their membership list (already loaded
  // app-wide) rather than the async search results, which would flicker.
  // Support users always need the picker to discover paidols beyond their own
  // memberships — even if they have zero PaidolUser rows.
  if (!showSwitcher) {
    return null;
  }

  return (
    <Box sx={sx}>
      <FormControl fullWidth size="small">
        {isUserOnboarded ? (
          <LoadMoreContext.Provider value={loadMoreContextValue}>
            <Autocomplete
              id="company-switcher"
              disabled={isSwitching}
              options={userCompanies}
              ListboxComponent={VirtualizedListbox}
              loading={supportSearchLoading}
              getOptionLabel={(option) => option?.name ?? ''}
              value={selectedPaidolValue}
              isOptionEqualToValue={(option, value) => option?.paidolId === value?.paidolId}
              // Case-insensitive substring filter. The server already filtered
              // with the same semantics, so this never shrinks real matches —
              // it only hides the injected current selection (and any stale
              // page while a fresh search is in flight) when it doesn't match
              // what was typed. MUI passes inputValue='' when the input is
              // merely displaying the current selection, so reopening still
              // shows the full list.
              filterOptions={(options, state) => {
                const query = state.inputValue.trim().toLowerCase();
                if (!query) return options;
                return options.filter((option) => option?.name?.toLowerCase().includes(query));
              }}
              onOpen={handleOpen}
              onClose={handleClose}
              onInputChange={handleInputChange}
              onChange={(_event, newValue) => {
                if (!newValue) return;
                handleChange(newValue);
              }}
              renderOption={(props, option, { inputValue }) => {
                // Highlight only an active search. After a selection (and on
                // reopen) MUI sets the input to the selected company's name —
                // that's display, not a search, and shouldn't bold anything.
                const searchText = inputValue === (selectedPaidolValue.name ?? '') ? '' : inputValue;
                return (
                  <li {...props} key={option?.paidolId}>
                    <span>{highlightMatch(option?.name ?? '', searchText)}</span>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  sx={{ padding: '0.5rem', background: '#fff' }}
                  {...params}
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSwitching || supportSearchLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </LoadMoreContext.Provider>
        ) : null}
      </FormControl>
    </Box>
  );
}

export default CompanySwitcher;
